import {
    afterNextRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    Injector,
    input,
    LOCALE_ID,
    output,
    signal,
    viewChild
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import {
    formatLocaleNumber,
    getLocaleSeparators,
    LocaleSeparators,
    parseLocaleNumber
} from './helper/number-format.helper';
import { noop, Subscription, timer } from 'rxjs';

@Component({
    selector: 'tran-number-input',
    templateUrl: './number-input.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'flex items-center pe-[7px]',
        '[style.--si-action-icon-offset]': "showButtons() ? '48px' : null"
    },
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: NumberInputComponent, multi: true },
        { provide: NG_VALIDATORS, useExisting: NumberInputComponent, multi: true }
    ]
})
export class NumberInputComponent implements ControlValueAccessor, Validator {
    private readonly generatedInputId = `__tran-number-input-${crypto.randomUUID()}`;

    public readonly min = input<number>();
    public readonly max = input<number>();
    public readonly step = input<number>(1);
    public readonly unit = input<string>();
    public readonly placeholder = input<string>();
    public readonly showButtons = input(true);
    public readonly readonly = input(false);
    public readonly inputId = input(this.generatedInputId);
    public readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });
    public readonly errorMessageId = input(`${this.generatedInputId}-errormessage`);

    public readonly valueChange = output<number | null>();

    protected readonly disabled = signal(false);
    protected readonly canIncrease = computed(() => {
        const max = this.max();
        return max === undefined || this.currentValue() === null || (this.currentValue() ?? 0) + this.step() <= max;
    });
    protected readonly canDecrease = computed(() => {
        const min = this.min();
        return min === undefined || this.currentValue() === null || (this.currentValue() ?? 0) - this.step() >= min;
    });

    private readonly localeId = inject(LOCALE_ID);
    private readonly injector = inject(Injector);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');
    private readonly currentValue = signal<number | null>(null);
    private readonly autoRepeat$ = timer(400, 80);
    private autoRepeatSub?: Subscription;

    private get locale(): string {
        return `${this.localeId}`;
    }

    private get separators(): LocaleSeparators {
        return getLocaleSeparators(this.locale);
    }

    private hasFormatError = false;
    private onChange: (value: number | null) => void = noop;
    private onTouched: () => void = noop;

    constructor() {
        afterNextRender(() => this.renderDisplayValue(), { injector: this.injector });
        this.destroyRef.onDestroy(() => this.stopAutoRepeat());
    }

    public writeValue(value: number | null): void {
        this.currentValue.set(value ?? null);
        this.hasFormatError = false;
        this.renderDisplayValue();
        this.changeDetectorRef.markForCheck();
    }

    public registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
    }

    public validate(_control: AbstractControl): ValidationErrors | null {
        return this.hasFormatError ? { numberFormat: true } : null;
    }

    protected onInput(): void {
        const raw = this.inputElement()?.nativeElement.value ?? '';
        const parsed = parseLocaleNumber(raw, this.separators);

        this.hasFormatError = !parsed.valid;
        this.currentValue.set(parsed.value);
        this.onChange(parsed.value);
        this.valueChange.emit(parsed.value);
    }

    protected onBlur(): void {
        this.onTouched();

        if (!this.hasFormatError) {
            this.renderDisplayValue();
        }
    }

    protected startAutoRepeat(event: Event, isIncrement: boolean): void {
        if (event instanceof MouseEvent && event.button) {
            return;
        }

        event.preventDefault();
        this.onTouched();

        const trigger = (): void => this.applyStep(isIncrement ? this.step() : -this.step());

        this.stopAutoRepeat();
        this.autoRepeatSub = this.autoRepeat$.subscribe(trigger);
        trigger();
    }

    protected stopAutoRepeat(): void {
        this.autoRepeatSub?.unsubscribe();
        this.autoRepeatSub = undefined;
    }

    private applyStep(delta: number): void {
        const next = this.clamp((this.currentValue() ?? 0) + delta);

        this.hasFormatError = false;
        this.currentValue.set(next);
        this.renderDisplayValue();
        this.onChange(next);
        this.valueChange.emit(next);

        if ((delta > 0 && !this.canIncrease()) || (delta < 0 && !this.canDecrease())) {
            this.stopAutoRepeat();
        }
    }

    private clamp(value: number): number {
        const min = this.min();
        const max = this.max();

        let result = value;
        if (min !== undefined) {
            result = Math.max(result, min);
        }
        if (max !== undefined) {
            result = Math.min(result, max);
        }

        return result;
    }

    private renderDisplayValue(): void {
        const input = this.inputElement()?.nativeElement;

        if (input) {
            input.value = formatLocaleNumber(this.currentValue(), this.locale);
        }
    }
}
